import { tokenStorage, type TokenData } from '../utils/tokenStorage';

const API_BASE_URL =
  import.meta.env.VITE_PLANNER_API_URL || 'http://localhost:3030';

export interface RefreshResult {
  ok: boolean;
  /**
   * True when the backend explicitly rejected the refresh token (401/403),
   * i.e. the session is definitively dead. False for network/transient errors
   * where the token might still be valid on a later retry.
   */
  authRejected: boolean;
  accessToken?: string;
  refreshToken?: string;
}

// Single in-flight refresh shared across ALL refresh paths (API 401 interceptor,
// store refreshAuth, periodic silentRefresh). The backend rotates refresh tokens
// (each /auth/refresh invalidates the previous one), so two concurrent refreshes
// would race and one would consume a token the other still needs — producing
// spurious 401s. Funnelling every refresh through this promise prevents that.
let inFlightRefresh: Promise<RefreshResult> | null = null;

async function performRefresh(): Promise<RefreshResult> {
  const attemptedRefreshToken = tokenStorage.getRefreshToken();
  if (!attemptedRefreshToken) {
    return { ok: false, authRejected: true };
  }

  // If another concurrent path already rotated the token, treat as success.
  const rotationRecovered = (): RefreshResult | null => {
    const latest = tokenStorage.getRefreshToken();
    if (latest && latest !== attemptedRefreshToken) {
      return {
        ok: true,
        authRejected: false,
        accessToken: tokenStorage.getAccessToken() ?? undefined,
        refreshToken: latest,
      };
    }
    return null;
  };

  try {
    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: attemptedRefreshToken }),
    });

    if (!response.ok) {
      const recovered = rotationRecovered();
      if (recovered) return recovered;
      return {
        ok: false,
        authRejected: response.status === 401 || response.status === 403,
      };
    }

    const json = await response.json().catch(() => null);
    const data = json?.data ?? json;

    if (data?.access_token && data?.refresh_token) {
      const tokenData: TokenData = {
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        accessTokenExpiresAt: data.expires_in
          ? Date.now() + data.expires_in * 1000
          : undefined,
      };
      tokenStorage.setTokens(tokenData);
      return {
        ok: true,
        authRejected: false,
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
      };
    }

    return { ok: false, authRejected: false };
  } catch (error) {
    console.error('Token refresh failed:', error);
    const recovered = rotationRecovered();
    if (recovered) return recovered;
    // Network / unexpected error — token may still be valid, don't nuke session.
    return { ok: false, authRejected: false };
  }
}

/**
 * Deduplicated access-token refresh. Returns the same in-flight promise while a
 * refresh is already running so the rotating, single-use refresh token is never
 * consumed by two concurrent callers.
 */
export function refreshAccessToken(): Promise<RefreshResult> {
  if (inFlightRefresh) {
    return inFlightRefresh;
  }

  inFlightRefresh = performRefresh().finally(() => {
    inFlightRefresh = null;
  });

  return inFlightRefresh;
}
